/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ICartIconViewProps, IFlyoutCartLineItemViewProps } from '@msdyn365-commerce-modules/cart';

//==========================================================
// @CUSTOM: Imports custom customer info and cart info to
// determine first time buyer and over limit or on hold
// customer status
//==========================================================
import { convertCustomerAttributes } from '../../../utilities/data-attribute-parser';
import { CustomerStatus } from '../../../settings/app.settings';
//==========================================================

const _renderCartlines = (cartLines: IFlyoutCartLineItemViewProps[] | undefined, props: ICartIconViewProps): JSX.Element[] | null => {
    if (!cartLines) {
        props.context.telemetry.error('Cartlines content is empty, module wont render');
        return null;
    }
    return cartLines.map((cartLine, index) => {
        return (
            <Node {...props.miniCartItemWrapper} key={index}>
                {cartLine.cartline}
                {cartLine.storeLocation}
                {cartLine.remove}
            </Node>
        );
    });
};

const CartIconView: React.FC<ICartIconViewProps> = (props: ICartIconViewProps) => {
    //======================================================
    // @CUSTOM: Removes checkout button if customer status
    // is first time buyer and over limit or customer status
    // is on hold
    //======================================================
    const customerData = props.data.customerInformation.result;

    const customerAttributes = convertCustomerAttributes(customerData?.Attributes || []);
    const onBoardingStatus = customerAttributes.CusOnboardStatus;
    const cartTotal = props.data.cart.result?.cart.TotalAmount;
    const creditLimit = props.context.app.config.firstTimeLimit;
    const customerStatusIds = props.context.app.config.customerStatus as CustomerStatus;

    const isBlocked = !!customerData?.Blocked;
    let isOnHold = false;
    let isNewCust = false;

    if (onBoardingStatus === customerStatusIds.customerOnHoldId) {
        isOnHold = true;
    }

    if (onBoardingStatus === customerStatusIds.customerFirstTimeBuyerId && cartTotal && cartTotal > creditLimit) {
        isNewCust = true;
    }
    //======================================================

    return (
        <Node {...props.miniCartWrapper} {...props.renderModuleAttributes(props)}>
            {props.cartIcon}
            {props.FlyoutContainer ? (
                <Node {...props.FlyoutContainer}>
                    {props.flyoutTitle}
                    <Node {...props.CartlinesWrapper}>
                        {_renderCartlines(props.cartlines, props)}
                    </Node>
                    {props.totalPrice}

                    {/* @CUSTOM: Remove checkout button based on customer status */}
                    {!isBlocked && !isNewCust && !isOnHold && props.checkoutAsSignInUserButton}
                    {/*----------------------------------------------------------*/}

                    {/* @CUSTOM: Remove guest checkout */}

                    {props.goToCartButton}
                </Node>
            ) : false }
        </Node>
    );
};

export default CartIconView;
